/**
 * Validation utilities for form fields
 */

export const validators = {
  /**
   * Validate email format
   */
  email: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Validate password strength
   * - Minimum 8 characters
   * - At least one uppercase letter
   * - At least one lowercase letter
   * - At least one number
   */
  password: (password: string): boolean => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  },

  /**
   * Validate password match
   */
  passwordMatch: (password: string, confirmPassword: string): boolean => {
    return password === confirmPassword && password.trim() !== "";
  },

  /**
   * Validate cedula (ID document)
   * - Must be 10 digits
   */
  cedula: (cedula: string): boolean => {
    const cedulaRegex = /^\d{10}$/;
    return cedulaRegex.test(cedula);
  },

  /**
   * Validate phone number
   * - Minimum 10 digits
   */
  phone: (phone: string): boolean => {
    const phoneRegex = /^\d{10,}$/;
    return phoneRegex.test(phone);
  },

  /**
   * Validate full name
   * - Not empty
   * - At least 3 characters
   */
  fullName: (fullName: string): boolean => {
    return fullName.trim().length >= 3;
  },

  /**
   * Validate address
   * - Not empty
   * - At least 5 characters
   */
  address: (address: string): boolean => {
    return address.trim().length >= 5;
  },
};

export interface FieldError {
  field: string;
  message: string;
}

/**
 * Get validation error messages
 */
export const getValidationError = (
  field: string,
  value: string,
  confirmValue?: string,
): string | null => {
  switch (field) {
    case "email":
      if (!value.trim()) return "El correo electrónico es requerido";
      if (!validators.email(value)) return "El correo electrónico no es válido";
      return null;

    case "password":
      if (!value.trim()) return "La contraseña es requerida";
      if (value.trim().length < 8)
        return "La contraseña debe tener al menos 8 caracteres";
      if (!validators.password(value))
        return "La contraseña debe contener mayúsculas, minúsculas y números";
      return null;

    case "confirmPassword":
      if (!value.trim()) return "Debe confirmar la contraseña";
      if (!validators.passwordMatch(confirmValue || "", value))
        return "Las contraseñas no coinciden";
      return null;

    case "fullName":
      if (!value.trim()) return "El nombre es requerido";
      if (!validators.fullName(value))
        return "El nombre debe tener al menos 3 caracteres";
      return null;

    case "cedula":
      if (!value.trim()) return "La cédula es requerida";
      if (!validators.cedula(value))
        return "La cédula debe tener exactamente 10 dígitos";
      return null;

    case "phone":
      if (!value.trim()) return "El celular es requerido";
      if (!validators.phone(value))
        return "El celular debe tener al menos 10 dígitos";
      return null;

    case "address":
      if (!value.trim()) return "La dirección es requerida";
      if (!validators.address(value))
        return "La dirección debe tener al menos 5 caracteres";
      return null;

    default:
      return null;
  }
};
